import { useEffect, useId, useMemo, useRef, useState } from 'react';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { cn } from '@/utils/cn';
import { CalendarPopover } from '@/components/ui/CalendarPopover';
import { Select } from '@/components/ui/Select';
import { FieldLabel, parseFieldLabel } from '@/components/ui/FieldLabel';
import {
  displayDateTime,
  formatDateTimeLocal,
  parseDateTimeLocal,
} from '@/utils/dateInput';
import { setHours, setMinutes } from 'date-fns';

export interface DateTimePickerProps {
  label?: string;
  error?: string;
  description?: string;
  value?: string;
  onChange?: (value: string) => void;
  min?: string;
  max?: string;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  className?: string;
  id?: string;
  name?: string;
}

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => ({
  value: String(h).padStart(2, '0'),
  label: String(h).padStart(2, '0'),
}));

const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, m) => ({
  value: String(m).padStart(2, '0'),
  label: String(m).padStart(2, '0'),
}));

export function DateTimePicker({
  label,
  error,
  description,
  value = '',
  onChange,
  min,
  max,
  disabled,
  required,
  placeholder = 'Select date & time',
  className,
  id,
  name,
}: DateTimePickerProps) {
  const autoId = useId();
  const fieldId = id ?? name ?? autoId;
  const { required: isRequired } = parseFieldLabel(label, required);
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const parsed = parseDateTimeLocal(value);
  const [viewMonth, setViewMonth] = useState(() => parsed ?? new Date());
  const [draftDate, setDraftDate] = useState<Date | null>(parsed);
  const [hour, setHour] = useState(() => (parsed ? String(parsed.getHours()).padStart(2, '0') : '09'));
  const [minute, setMinute] = useState(() => (parsed ? String(parsed.getMinutes()).padStart(2, '0') : '00'));

  useEffect(() => {
    const next = parseDateTimeLocal(value);
    if (next) {
      setViewMonth(next);
      setDraftDate(next);
      setHour(String(next.getHours()).padStart(2, '0'));
      setMinute(String(next.getMinutes()).padStart(2, '0'));
    }
  }, [value]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const display = useMemo(() => displayDateTime(value), [value]);

  const apply = () => {
    if (!draftDate) return;
    const next = setMinutes(setHours(draftDate, Number(hour)), Number(minute));
    onChange?.(formatDateTimeLocal(next));
    setOpen(false);
  };

  const pickDate = (date: Date) => {
    setDraftDate(date);
  };

  return (
    <div className={cn('relative min-w-0 w-full', className)} ref={rootRef}>
      <FieldLabel htmlFor={fieldId} label={label} required={required} />
      <button
        type="button"
        id={fieldId}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={cn(
          'input flex w-full min-w-0 items-center gap-2 text-left',
          !display && 'text-slate-500 dark:text-slate-400',
          display && 'text-slate-900 dark:text-white',
          error && 'input-error',
          disabled && 'cursor-not-allowed opacity-60',
        )}
      >
        <ScheduleIcon className="shrink-0 text-primary-600 dark:text-primary-400" sx={{ fontSize: 18 }} />
        <span className="truncate">{display || placeholder}</span>
      </button>
      {name && <input type="hidden" name={name} value={value} required={isRequired} readOnly />}
      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-[min(18rem,calc(100vw-2rem))] space-y-2">
          <CalendarPopover
            value={draftDate ? formatDateTimeLocal(draftDate).slice(0, 10) : ''}
            viewMonth={viewMonth}
            onViewMonthChange={setViewMonth}
            onSelect={pickDate}
            min={min?.slice(0, 10)}
            max={max?.slice(0, 10)}
          />
          <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-lg dark:border-slate-600 dark:bg-slate-900">
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Time</p>
            <div className="form-grid-2">
              <Select label="Hour" options={HOUR_OPTIONS} value={hour} onChange={setHour} />
              <Select label="Minute" options={MINUTE_OPTIONS} value={minute} onChange={setMinute} />
            </div>
            <button
              type="button"
              className="btn-primary mt-3 w-full"
              disabled={!draftDate}
              onClick={apply}
            >
              Apply
            </button>
          </div>
        </div>
      )}
      {description && !error && (
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{description}</p>
      )}
      {error && (
        <p className="mt-1.5 text-xs text-error-700 dark:text-red-400" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export function defaultDateTimeLocalValue(offsetHours = 1): string {
  const d = new Date();
  d.setHours(d.getHours() + offsetHours, 0, 0, 0);
  return formatDateTimeLocal(d);
}
